// askSmart-ui/src/pages/MapleCoin/components/ActivityList.tsx
import { ArrowDownLeft, ArrowUpRight, Undo2, ChevronRight, Receipt } from 'lucide-react';
import { Transaction, TransactionStatus } from '../types';
import { formatSignedAmount } from '../money';

// Customer-facing descriptions of transaction types. Business meaning only —
// no accounting terms (debit/credit, ledger codes, journals) on this list.
const DESCRIPTIONS: Record<string, string> = {
  SANDBOX_DEPOSIT: 'Sandbox Deposit',
  SANDBOX_PENDING_DEPOSIT: 'Sandbox Deposit',
  SANDBOX_WITHDRAWAL: 'Sandbox Withdrawal',
  REVERSAL: 'Reversal',
};

export const STATUS_LABEL: Record<TransactionStatus, string> = {
  CREATED: 'Pending',
  PENDING: 'Pending',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
  REVERSED: 'Reversed',
};

export function describe(t: Transaction): string {
  return DESCRIPTIONS[t.type] ?? (t.direction === 'CREDIT' ? 'Money in' : 'Money out');
}

function dayLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-CA', {
    weekday: 'long', month: 'long', day: 'numeric',
    year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric',
  });
}

/** Grouped under a day heading, as a bank statement reads (input is newest first). */
function groupByDay(transactions: Transaction[]): Array<[string, Transaction[]]> {
  const groups = new Map<string, Transaction[]>();
  for (const t of transactions) {
    const label = dayLabel(t.createdAt);
    groups.set(label, [...(groups.get(label) ?? []), t]);
  }
  return Array.from(groups.entries());
}

function Row({ t }: { t: Transaction }) {
  const Icon = t.type === 'REVERSAL' ? Undo2 : t.direction === 'CREDIT' ? ArrowDownLeft : ArrowUpRight;
  const settled = t.status === 'COMPLETED';
  const inactive = t.status === 'FAILED' || t.status === 'REVERSED';
  const time = new Date(t.createdAt).toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit' });
  return (
    <li className={`mc-tx${inactive ? ' mc-tx--muted' : ''}`} data-reference={t.reference}>
      <span className="mc-tx-icon"><Icon size={18} /></span>
      <div className="mc-tx-text">
        <div className="mc-tx-title">{describe(t)}</div>
        <div className="mc-tx-sub">
          {t.reference} · {time}
        </div>
      </div>
      <div className="mc-tx-amounts">
        <div className={`mc-tx-amount${t.direction === 'CREDIT' && !inactive ? ' mc-tx-amount--in' : ''}`}>
          {formatSignedAmount(t.amountMinor, t.currency, t.direction)}
        </div>
        <div className={`mc-tx-status mc-tx-status--${t.status.toLowerCase()}${settled ? '' : ' mc-tx-status--flag'}`}>
          {STATUS_LABEL[t.status]}
        </div>
      </div>
    </li>
  );
}

export default function ActivityList({
  transactions,
  limit,
  onViewAll,
  onLoadMore,
  loadingMore = false,
  emptyBody = 'Your MapleCoin transactions will appear here.',
}: {
  /** Newest first, as the API returns them. */
  transactions: Transaction[];
  /** Show only the most recent N, with a link to the rest. */
  limit?: number;
  onViewAll?: () => void;
  /** Present when older transactions can be fetched. */
  onLoadMore?: () => void;
  loadingMore?: boolean;
  emptyBody?: string;
}) {
  if (transactions.length === 0) {
    return (
      <div className="mc-empty">
        <span className="mc-empty-icon"><Receipt size={22} /></span>
        <div className="mc-empty-title">No transactions yet</div>
        <p>{emptyBody}</p>
      </div>
    );
  }

  const shown = limit ? transactions.slice(0, limit) : transactions;

  return (
    <>
      {groupByDay(shown).map(([day, items]) => (
        <div key={day} className="mc-tx-group">
          <div className="mc-tx-day">{day}</div>
          <ul>{items.map((t) => <Row key={t.reference} t={t} />)}</ul>
        </div>
      ))}
      {onViewAll && (
        <button type="button" className="mc-link mc-activity-all" onClick={onViewAll}>
          View all transactions <ChevronRight size={16} />
        </button>
      )}
      {onLoadMore && (
        <button type="button" className="mc-link mc-activity-all" onClick={onLoadMore} disabled={loadingMore}>
          {loadingMore ? 'Loading…' : 'Show older transactions'}
        </button>
      )}
    </>
  );
}
